import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { get, omit } from "lodash";
import { RootState } from "../../../../redux/store";
import { SurveyComponent } from "../../../../components/surveyjs/survey/survey.component";
import { Layout } from "../../../../layout/layout";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { StatusCode } from "../../../../components/error-state/error-state";
import bannerDefault from "../form/img/banner.png";
import svgDone from "../form/img/done.svg";
import { StatusEnum, getI18nData, convert2SurveyjsI18nData } from "./form.util";
import { ErrorScreen } from "../../../../components/error-screen/error-screen";
import { getARLConfig, submit } from "../../network/network";
import { AxiosError } from "axios";
import { useParams, useSearchParams } from "react-router-dom"; // plai changes
import "./form.css";
import { TakeUIClickEventParameters } from "../../../../common/ga/types/ga-general-type";
import { takeUIClickEvent } from "../../../../common/ga/ga";
import { eventTrackingService } from "../../../../utils/event-tracking/event-tracking.service";
import {
  TrackingInfo,
  TrackingEventType,
  EventFeature,
  EventModule,
} from "../../../../utils/event-tracking/event-tracking.model";
import { bodyText, navBarStyles } from "./form.style";
import { Dialog, DialogTitle, Button, Typography, Box } from "@mui/material";

//plai change start
import PlaiHeader from "../../../../components/plai-header/plai-header";
import PlaiFooter from "../../../../components/plai-footer/plai-footer";
import SalesAgent from "./components/sales-agent/sales-agent";
import { LangOptionComponent } from "../../../../layout/lang-option/lang-option.component";
import { GlobalHelper } from "../../../../utils/helpers/global-helper";
import { commonSlice } from "../../../../redux/common/common-slice";
import { PlaiErrorScreen } from "../../../../components/plai-error-screen/plai-error-screen";
const { showLoadingAction, hideLoadingAction } = commonSlice.actions;
//plai change end

enum ARL_REDIRECTION_TYPE {
  URL = "GIVEN_URL",
  DIRECT_MANAGER = "DIRECT_MANAGER_URL",
}

export const SurveyFormScreen: React.FC = () => {
  const [errorStatus, setErrorStatus] = useState<StatusCode | "0">("0");
  const [submitStatus, setSubmitStatus] = useState<StatusEnum>(StatusEnum.IDLE);
  const [showDirectDialog, setShowDirectDialog] = useState(false);
  const [inactiveAgentErrorData, setInactiveAgentErrorData] = useState<any>();

  const { t } = useTranslation();
  const curLang = i18next.language;

  const params = useParams();
  // const queryParams = new URLSearchParams(window.location.search);

  // plai change start
  const [queryParams, setQueryParams] = useSearchParams();
  const redirectTo = queryParams.get("redirectTo");
  const [data, setData] = useState<{ ui: any }>();
  const [surveyJson, setSurveyJson] = useState<any>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    GlobalHelper.getGlobalDispatch()?.(loading ? showLoadingAction() : hideLoadingAction());
  }, [loading]);

  useEffect(() => {
    if (!params.refId) {
      return;
    }
    setLoading(true);
    const actualId = queryParams.get("refId") ?? undefined;
    getARLConfig(params.refId, actualId)
      .then((res: any) => {
        const jsonCopy = get(res, "ui.inputFields");
        // const jsonCopy = { ...json };
        if (jsonCopy) {
          const callToAction = get(res, "ui.content.callToAction");
          let tncList = get(res, "ui.content.tncList", []);
          // set complete btn text
          jsonCopy.completeText = convert2SurveyjsI18nData(callToAction);
          // add tnc
          tncList = convert2SurveyjsI18nData(tncList);
          if (tncList) {
            const tnc = {
              type: "tnc",
              name: "tnc",
              titleLocation: "hidden",
              choices: tncList,
            } as any;
            jsonCopy.pages[0].elements.push(tnc);
          }
          if (res?.ui?.sourceId === "sharia") {
            jsonCopy.pages[0].elements[0].title.id = t("is_customer_sharia");
          }
          setSurveyJson(jsonCopy);
        }
        setData(res);
      })
      .catch((err: AxiosError) => {
        const status = get(err, "response.status", "-1"); // -1: unknown error by default
        setErrorStatus(status.toString() as StatusCode);
        // hanldleError(err);
        // setSubmitStatus(StatusEnum.FAILED);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params.refId]);
  // plai change end

  /** kafka event(not GA) tracking */
  const submitEvent = useCallback((refId: string, resourceUuid: string | null) => {
    if (!resourceUuid) {
      return;
    }
    const postBody: TrackingInfo = {
      subject: "anonymous:ref=refId",
      data: {
        context: {
          src: "publicweb",
          uuid: "",
          userAgent: window.navigator.userAgent,
        },

        name: "prospect::submit_leadform",
        module: EventModule.resource,
        feature: EventFeature.resource,
        target: resourceUuid,
        params: {
          startDatetime: Date.now(),
          refId: refId,
          sessionId: sessionStorage.getItem("session_id") || "",
          target: "submit_referral_link_form", // click target
        },
        type: TrackingEventType.click,
      },
    };
    eventTrackingService.doReport(postBody, true);
  }, []);

  const submitForm = useCallback(
    (formData: any) => {
      if (!params.refId) {
        return;
      }
      // add resource fields for lead-dist
      const resourceUuid = queryParams.get("uuid");
      const actualId = queryParams.get("refId") ?? undefined;
      if (resourceUuid) {
        let resourceName: string | undefined = queryParams.get("name") || undefined;
        if (resourceName) {
          resourceName = decodeURIComponent(resourceName);
        }
        formData.resourceUuid = resourceUuid;
        formData.resourceName = resourceName;
        formData.refId = actualId ?? params.refId; // for hash-entry data backtracking
      }
      //plai change start
      formData.phoneNumber.number = validatePhone(
        formData.phoneNumber.region.key,
        formData.phoneNumber.number,
      );
      setLoading(true);
      submit(actualId ?? params.refId, formData, actualId)
        .then(() => {
          setSubmitStatus(StatusEnum.DONE);
          setLoading(false);
        })
        .catch((err: AxiosError) => {
          const status = get(err, "response.status", "-1"); // -1: unknown error by default
          setErrorStatus(status.toString() as StatusCode);
          setLoading(false);
        });
      //plai change end
      // debug status
      // setSubmitStatus(StatusEnum.DONE);
      submitEvent(actualId ?? params.refId, resourceUuid);
      const gaData: TakeUIClickEventParameters = {
        module: "LeadForm",
        feature: "ReferralLink",
        screen_name: "ReferralLinkPublicPortalForm",
        journey: "referral_link_form_submission",
        stage: "submit_referral_link_form",
        object_name: "CTA",
        object_type: "BUTTON",
      };
      takeUIClickEvent(gaData);
    },
    [params.refId],
  );

  //plai change start
  const validatePhone = (codeCountry: string, phoneNumber: string) => {
    let removeSpace = phoneNumber.replace(/\s/g, "");
    //condition 1 when phone number contain +6212345
    const checkCondition1 = removeSpace.split(codeCountry);
    if (checkCondition1.length > 1) {
      return checkCondition1[1];
    }
    //condition 2 when phone number contain 012345
    if (removeSpace[0] === "0") {
      return removeSpace.split("0")[1];
    }
    const countryCodeWithoutPlus = codeCountry.substring(1);
    //condition 3 when phone number contain 6212345
    const checkCondition3 =
      removeSpace.substring(0, countryCodeWithoutPlus.length) === countryCodeWithoutPlus;
    if (checkCondition3) {
      const slice = removeSpace.substring(countryCodeWithoutPlus.length, removeSpace.length);
      return slice;
    }
    return removeSpace;
  };

  const navStyle = navBarStyles();
  //plai change end

  if (errorStatus !== "0" && !inactiveAgentErrorData) {
    return (
      <>
        <PlaiErrorScreen withFooter />
      </>
    );
  }

  return (
    <>
      {!loading && data && (
        <Layout haveNavbar disableLanguageButton>
          {/* plai change start */}
          <PlaiHeader imgUrl={data?.ui.content.headerLogoLink} />
          {/* plai change end */}

          <div className={`root-wrap ${navStyle.rootContainer}`}>
            <div className="banner">
              <img alt="banner" src={get(data, "ui.content.bannerLink", bannerDefault)} />
            </div>
            <LangOptionComponent haveNavbar customeStyle={navStyle.switchContainer} />

            {data && (
              <div className="content">
                <h1 className="title">{getI18nData(get(data, "ui.content.title"), curLang)}</h1>
                <p className="sub-title">
                  {getI18nData(get(data, "ui.content.subTitle"), curLang)}
                </p>

                {/* submit done */}
                <div
                  className={
                    submitStatus === StatusEnum.DONE
                      ? "flex flex-col w-full h-full overflow-x-hidden items-center justify-center mt-16"
                      : "hidden"
                  }
                >
                  <div>
                    <img alt="submit-done" className="mt-4 h-auto" src={svgDone} />
                  </div>
                  <div className="text-md lg:text-2xl font-bold pt-6">
                    {getI18nData(get(data, "ui.submissionResponse.title"), curLang)}
                  </div>
                  <div className="text-sm lg:text-lg text-center px-6 pt-2 text-gray-400">
                    {getI18nData(get(data, "ui.submissionResponse.content"), curLang)}
                  </div>
                </div>
                <div className={submitStatus !== StatusEnum.IDLE ? "hidden" : ""}>
                  <div
                    style={bodyText}
                    className="body"
                    dangerouslySetInnerHTML={{
                      __html: getI18nData(get(data, "ui.content.body"), curLang),
                    }}
                  />
                  <div className="flex-container top-line" />
                </div>
                {/** content end */}
              </div>
            )}
            {submitStatus === StatusEnum.IDLE && surveyJson && (
              <SurveyComponent onSubmit={submitForm} json={surveyJson} />
            )}
            {get(data, "ui.showProfileInReferralLink", false) && (
              <SalesAgent refId={queryParams.get("refId") || params.refId || ""} />
            )}
          </div>

          {/* plai change start */}
          <PlaiFooter content={getI18nData(get(data, "ui.content.footerWording"), curLang)} />
          {/* plai change end */}
        </Layout>
      )}
    </>
  );
};
